import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { Row, Card, Col, Divider, Empty, Skeleton, List, Image, Typography, Button, Modal, message, Tag, Spin, Tooltip } from "antd";
import { BulbOutlined, ExportOutlined, SyncOutlined, PrinterOutlined } from "@ant-design/icons";
import PageHeader from "../../components/PageHeader";
import * as SingleRequestServices from '../../services/SingleRequest';
import { generateInspectionGuide, getInspectionGuide } from "../../services/aiAssistantService";
import { BsStars } from "react-icons/bs";
import "./FlooringManufacturerRequest";
import FlooringManufacturerRequest from "./FlooringManufacturerRequest";

function SingleInspectionRequest() {
  var dispatch = useDispatch();
  const userDetails = useSelector((state) => state?.User?.userDetails);
  const [loading, setLoading] = useState(true);
  const [requestData, setRequestData] = useState([]);
  const [attachments, setAttachments] = useState(null);
  const [aiGuideVisible, setAiGuideVisible] = useState(false);
  const [aiGuideLoading, setAiGuideLoading] = useState(false);
  const [aiGuideData, setAiGuideData] = useState(null);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  const loadingMessages = [
    "Analyzing inspection details...",
    "Reviewing manufacturer standards...",
    "Cross-referencing industry guidelines...",
    "Identifying critical focus areas...",
    "Generating expert photo requirements...",
    "Finalizing your custom checklist..."
  ];

  useEffect(() => {
    let interval;
    if (aiGuideLoading) {
      interval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 2500);
    } else {
      setLoadingMessageIndex(0);
    }
    return () => clearInterval(interval);
  }, [aiGuideLoading]);

  const handleGenerateGuide = async () => {
    const queryParams = new URLSearchParams(window.location.search);
    const req_id = queryParams.get('req_id');

    // 1. Check if guide already exists to show instantly
    try {
      const existingResp = await getInspectionGuide(req_id);
      if (existingResp?.data?.status === 'completed' || existingResp?.data?.guide_content) {
        setAiGuideData(existingResp);
        setAiGuideVisible(true);
        setAiGuideLoading(false);
        return; // Instant retrieval!
      }
    } catch (err) {
      console.info("No existing guide found, proceeding to generate.");
    }

    // 2. If no existing guide, start generation
    triggerGeneration(req_id);
  };

  const handleRegenerate = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const req_id = queryParams.get('req_id');
    triggerGeneration(req_id);
  };

  const handlePrint = () => {
    if (!aiGuideData?.data?.guide_content) return;

    const queryParams = new URLSearchParams(window.location.search);
    const req_id = queryParams.get('req_id');
    const content = aiGuideData.data.guide_content;

    // Create a hidden iframe for professional printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const html = `
      <html>
        <head>
          <title>AI Inspection Guide - Floor Detective</title>
          <style>
            @page {
              margin: 1.5cm 1.2cm !important; /* Proper margins for every page */
            }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; 
              padding: 0 !important; /* Margin handles the spacing now */
              color: #333; 
              line-height: 1.5 !important; 
              background: #fff;
              font-size: 14.5px !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important; 
            }
            .header-main { border-bottom: 3px solid #0a497b; padding-bottom: 10px; margin-bottom: 20px; }
            h1 { color: #0a497b; margin: 0; font-size: 26px !important; }
            .meta-info { margin-top: 5px; color: #666; font-size: 14px; }
            
            h2 { color: #0a497b; margin-top: 25px; border-left: 5px solid #0a497b; padding-left: 12px; font-size: 19px !important; margin-bottom: 12px; }
            
            .tool-container { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
            .tool-tag { background: #e6f7ff; color: #1890ff; padding: 4px 12px; border-radius: 15px; font-size: 12px; font-weight: 500; border: 1px solid #91d5ff; }
            
            .focus-card { 
              border: 1px solid #e8e8e8; border-radius: 10px; margin-bottom: 20px; 
              overflow: hidden; box-shadow: 0 1px 5px rgba(0,0,0,0.05); page-break-inside: avoid;
            }
            .card-header { 
              background: #f0f5ff !important; padding: 12px 18px; border-bottom: 1px solid #e8e8e8;
              display: flex; justify-content: space-between; align-items: center;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .card-title { font-weight: bold; font-size: 16px !important; color: #003a8c; }
            
            .priority-tag { 
              padding: 3px 12px; border-radius: 4px; font-size: 10px; font-weight: bold; color: white !important; text-transform: uppercase;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .critical { background-color: #f5222d !important; }
            .high { background-color: #fa8c16 !important; }
            .medium { background-color: #1890ff !important; }
            .info { background-color: #1890ff !important; }
            
            .card-body { padding: 18px; }
            .why-label { color: #595959; font-size: 14px; font-style: italic; margin-bottom: 10px; display: block; }
            
            .checklist-ul { padding-left: 20px; margin: 10px 0; }
            .checklist-li { margin-bottom: 5px; font-size: 14px; list-style-type: disc; }
            
            .photo-box { 
              background: #f6ffed !important; padding: 12px 15px; border: 1px solid #b7eb8f; 
              border-radius: 8px; margin-top: 15px;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .photo-label { color: #389e0d; font-weight: bold; font-size: 13px; display: block; margin-bottom: 6px; }
            .photo-tag { 
              display: inline-block; background: white !important; border: 1px solid #b7eb8f; 
              padding: 2px 8px; border-radius: 4px; margin-right: 6px; font-size: 12px; color: #389e0d;
            }

            .standards-box { background: #f9f0ff !important; border: 1px solid #d3adf7; padding: 15px; border-radius: 8px; }
            .standard-item { margin-bottom: 5px; color: #531dab; font-size: 14px; }

            @media print {
              footer { border-top: 1px solid #eee; margin-top: 20px; padding-top: 10px; text-align: center; }
            }
          </style>
        </head>
        <body>
          <div class="header-main">
            <h1>AI Inspection Guide</h1>
            <div class="meta-info">
              <span><strong>Inspection ID:</strong> ${req_id}</span> | 
              <span><strong>Claim Number:</strong> ${requestData?.claim_number || 'N/A'}</span>
            </div>
          </div>
          
          <div class="section" style="margin-bottom: 15px;">
            <h2 style="margin-top: 0;">Required Tools & Equipment</h2>
            <div class="tool-container">
              ${content.required_tools?.map(tool => `<div class="tool-tag">${tool}</div>`).join('') || 'None listed'}
            </div>
          </div>

          <div class="section">
            <h2 style="margin-top: 10px;">Key Inspection Focus Areas</h2>
            ${content.inspection_focus_areas?.map(area => `
              <div class="focus-card">
                <div class="card-header">
                  <span class="card-title">${area.category || 'Focus Area'}</span>
                  <span class="priority-tag ${area.priority?.toLowerCase() || 'info'}">${area.priority}</span>
                </div>
                <div class="card-body">
                  <span class="why-label"><strong>Why important:</strong> ${area.why_important}</span>
                  <ul class="checklist-ul">
                    ${area.checklist?.map(item => `<li class="checklist-li">${item}</li>`).join('')}
                  </ul>
                  ${area.photo_requirements?.length > 0 ? `
                    <div class="photo-box">
                      <span class="photo-label">Photo Requirements :</span>
                      ${area.photo_requirements.map(photo => `<span class="photo-tag">${photo}</span>`).join('')}
                    </div>
                  ` : ''}
                </div>
              </div>
            `).join('')}
          </div>

          ${content.industry_standards_to_reference?.length > 0 ? `
            <div class="section" style="margin-top: 10px;">
               <h2 style="margin-top: 15px;">Suggested Industry Standards</h2>
               <div class="standards-box">
                 ${content.industry_standards_to_reference.map(std => `<div class="standard-item">• ${std}</div>`).join('')}
               </div>
            </div>
          ` : ''}

          <footer style="margin-top: 30px; text-align: center; font-size: 10px; color: #999; border-top: 1px dotted #ccc; padding-top: 15px;">
            Generated by Floor Detective AI Assistant | Confidential Inspection Guidance
          </footer>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 500);
            };
          </script>
        </body>
      </html>
    `;

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    // Clean up the iframe after a delay to ensure printing is triggered
    setTimeout(() => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }, 2000);
  };


  const triggerGeneration = async (req_id) => {
    setAiGuideVisible(true);
    setAiGuideLoading(true);
    setAiGuideData(null);

    try {
      // 1. Initial trigger to start generation
      await generateInspectionGuide(req_id, requestData, requestData?.project_id || null);

      // 2. Poll for completion
      let attempts = 0;
      const maxAttempts = 30; // 60 seconds max (2s * 30)

      const poll = async () => {
        try {
          const resp = await getInspectionGuide(req_id);
          if (resp?.data?.status === 'completed' || resp?.data?.guide_data) {
            setAiGuideData(resp);
            setAiGuideLoading(false);
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, 2000); // Check again in 2 seconds
          } else {
            message.info("AI analysis is still in progress. You can close this and click the button again in a few seconds to see the result.");
            setAiGuideLoading(false);
          }
        } catch (err) {
          console.error("Polling error:", err);
          setAiGuideLoading(false);
        }
      };

      poll();

    } catch (error) {
      console.error("Guide generation error:", error);
      message.error("Failed to start AI Inspection Guide generation.");
      setAiGuideLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
    setAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fetchData = (params = {}) => {
    const queryParams = new URLSearchParams(window.location.search);
    const req_id = queryParams.get('req_id');

    setLoading(true);
    dispatch(SingleRequestServices.getSingleRequest(req_id))
      .then((res) => {
        setRequestData(res);
        setLoading(false);
      })
      .catch((err) => {
        console.info('Get Error Edit State...');
        setLoading(false);
      });
  };

  useEffect(() => {
    setAttachments(requestData?.img_array);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestData?.img_array]);

  return (
    <div className="singleReportWrapper">
      <div className="pageHeader">
        <PageHeader title="Single Inspection Request" type="secondary" />
      </div>
      <div className="container-xxl">
        {userDetails?.user_role === 'um_inspector' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '20px', paddingBottom: '20px' }}>
            <Button
              type="primary"
              size="large"
              icon={<BsStars style={{ marginRight: '8px' }} />}
              onClick={handleGenerateGuide}
              style={{
                background: 'linear-gradient(135deg, #722ed1 0%, #391085 100%)',
                borderColor: '#722ed1',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(114, 46, 209, 0.3)',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              Generate AI Checklist
            </Button>
          </div>
        )}
        <Modal
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '95%' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BulbOutlined style={{ color: '#faad14' }} /> AI Inspection Guidelines
              </span>
              {!aiGuideLoading && aiGuideData && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    size="small"
                    onClick={handlePrint}
                    icon={<PrinterOutlined />}
                    style={{
                      borderRadius: '6px',
                      color: '#0a497b',
                      borderColor: '#0a497b',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      borderWidth: '1px'
                    }}
                  >
                    Print
                  </Button>
                  <Button
                    size="small"
                    onClick={handleRegenerate}
                    icon={<SyncOutlined />}
                    style={{
                      borderRadius: '6px',
                      color: '#0a497b',
                      borderColor: '#0a497b',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      borderWidth: '1px'
                    }}
                  >
                    Regenerate
                  </Button>
                </div>
              )}
            </div>
          }
          visible={aiGuideVisible}
          onCancel={() => setAiGuideVisible(false)}
          footer={null}
          width={850}
          centered
          bodyStyle={{
            maxHeight: '75vh',
            overflowY: 'auto',
            padding: '24px',
            backgroundColor: '#f8f9fa'
          }}
        >
          <div style={{
            minHeight: '300px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: aiGuideLoading ? 'center' : 'stretch',
            justifyContent: aiGuideLoading ? 'center' : 'flex-start'
          }}>
            {aiGuideLoading ? (
              <div style={{ textAlign: 'center' }}>
                <div className="ai-pulse-container" style={{ marginBottom: '24px' }}>
                  <BsStars style={{ fontSize: '60px', color: '#0a497b', animation: 'ai-pulse 2s infinite ease-in-out' }} />
                </div>
                <Typography.Text strong style={{ fontSize: '16px', color: '#0a497b', display: 'block', transition: 'all 0.5s' }}>
                  {loadingMessages[loadingMessageIndex]}
                </Typography.Text>
                <div style={{ width: '200px', margin: '15px auto 0' }}>
                  <div style={{ height: '3px', width: '100%', background: '#f0f0f0', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      background: 'linear-gradient(90deg, #0a497b, #1890ff)',
                      width: '100%',
                      animation: 'ai-progress-bar 2s infinite ease-in-out'
                    }}></div>
                  </div>
                </div>
                <style>{`
                    @keyframes ai-pulse {
                        0% { transform: scale(1); opacity: 0.6; filter: drop-shadow(0 0 0px #0a497b); }
                        50% { transform: scale(1.2); opacity: 1; filter: drop-shadow(0 0 15px #0a497b); }
                        100% { transform: scale(1); opacity: 0.6; filter: drop-shadow(0 0 0px #0a497b); }
                    }
                    @keyframes ai-progress-bar {
                        0% { transform: translateX(-100%); }
                        100% { transform: translateX(100%); }
                    }
                `}</style>
              </div>
            ) : aiGuideData?.data?.guide_content ? (
              <div style={{ padding: '10px 0' }}>
                <div style={{ background: '#f0f5ff', padding: '16px', borderRadius: '12px', marginBottom: '24px' }}>
                  <Typography.Title level={5} style={{ color: '#003a8c', marginBottom: '12px' }}>
                    <i className="fa fa-wrench" style={{ marginRight: '8px' }}></i> Required Tools & Equipment
                  </Typography.Title>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {aiGuideData.data?.guide_content?.required_tools?.map((tool, idx) => (
                      <Tag key={idx} color="blue" bordered={false} style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '13px' }}>{tool}</Tag>
                    )) || <p>No specific tools listed.</p>}
                  </div>
                </div>

                <Typography.Title level={4} style={{ marginBottom: '16px', borderLeft: '4px solid #722ed1', paddingLeft: '12px' }}>
                  Key Inspection Focus Areas
                </Typography.Title>

                {aiGuideData.data?.guide_content?.inspection_focus_areas?.map((area, idx) => (
                  <Card
                    size="small"
                    className="ai-focus-card"
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '15px', fontWeight: 'bold' }}>{area.category || 'Area'}</span>
                        <Tag color={area.priority === 'CRITICAL' ? '#f5222d' : area.priority === 'HIGH' ? '#fa8c16' : '#1890ff'} style={{ borderRadius: '4px' }}>
                          {area.priority}
                        </Tag>
                      </div>
                    }
                    key={idx}
                    style={{
                      marginBottom: '16px',
                      borderRadius: '10px',
                      border: '1px solid #e8e8e8',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                    }}
                  >
                    <div style={{ padding: '4px' }}>
                      <p style={{ color: '#595959', fontSize: '14px', fontStyle: 'italic', marginBottom: '12px' }}>
                        <strong>Why important:</strong> {area.why_important}
                      </p>
                      <ul style={{ paddingLeft: '20px', marginBottom: '12px' }}>
                        {area.checklist?.map((item, i) => (
                          <li key={i} style={{ marginBottom: '6px', fontSize: '14px' }}>{item}</li>
                        ))}
                      </ul>
                      {area.photo_requirements?.length > 0 && (
                        <div style={{ background: '#f6ffed', padding: '8px 12px', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                          <strong style={{ fontSize: '13px', color: '#389e0d' }}>Required Photos:</strong>
                          <div style={{ marginTop: '6px' }}>
                            {area.photo_requirements.map((photo, pIdx) => (
                              <Tag key={pIdx} color="success" style={{ marginBottom: '4px' }}>{photo}</Tag>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}

                {aiGuideData.data?.guide_content?.industry_standards_to_reference && (
                  <div style={{ marginTop: '24px', padding: '16px', borderTop: '1px dashed #d9d9d9' }}>
                    <Typography.Title level={5} style={{ color: '#0a497b', marginBottom: '12px' }}>
                      <i className="fa fa-book" style={{ marginRight: '8px' }}></i> Suggested Industry Standards
                      <span style={{ fontSize: '12px', fontWeight: 'normal', fontStyle: 'italic', marginLeft: '10px', color: '#8c8c8c' }}>
                        (Click to view details)
                      </span>
                    </Typography.Title>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {aiGuideData.data.guide_content.industry_standards_to_reference.map((std, idx) => (
                        <Tooltip title={`Search for ${std} manual`} key={idx}>
                          <Tag
                            color="purple"
                            style={{
                              marginBottom: 8,
                              cursor: 'pointer',
                              padding: '8px 14px',
                              borderRadius: '6px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                              transition: 'all 0.3s',
                              whiteSpace: 'normal',
                              height: 'auto',
                              display: 'inline-block',
                              maxWidth: '100%',
                              lineHeight: '1.4'
                            }}
                            className="clickable-standard-tag"
                          >
                            <a
                              href={`https://www.google.com/search?q=${encodeURIComponent(std)}+installation+standard+pdf`}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                            >
                              {std} <ExportOutlined style={{ marginLeft: '8px', fontSize: '12px', color: '#0a497b' }} />
                            </a>
                          </Tag>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {!aiGuideLoading && <p>No guide data available yet.</p>}
              </div>
            )}
          </div>
        </Modal>
        {loading ?
          (
            <div className="">
              <Row gutter={16} className="mb-1 mt-5 skel_req">
                <Col xs={24} md={2} xl={2} className="">
                  <Skeleton.Input block={true} active size="large" style={{ height: "120px" }} />
                </Col>
                <Col xs={24} md={2} xl={2} className="">

                </Col>
                <Col xs={24} md={3} xl={3} className="">
                  <Skeleton.Input block={true} active size="small" />
                  <hr></hr>
                </Col>
                <hr></hr>
                <Col xs={24} md={17} xl={17} className="">
                </Col>
              </Row>

              <br></br>
              <hr />
              <br></br>
              <Skeleton loading={loading} active >
                <List.Item.Meta
                />
              </Skeleton>
              <br></br>
              <hr />
              <br></br>
              <Skeleton loading={loading} active >
                <List.Item.Meta
                />
              </Skeleton>
              <br></br>
              <hr />
              <br></br>
              <Skeleton loading={loading} active >
                <List.Item.Meta
                />
              </Skeleton>
              <br></br>
              <hr />
              <br></br>
              <Skeleton loading={loading} active >
                <List.Item.Meta
                />
              </Skeleton>
              <br></br>
              <hr />
              <br></br>
              <Skeleton loading={loading} active >
                <List.Item.Meta
                />
              </Skeleton>
              <br></br>
              <hr />
              <br></br>
              <Skeleton loading={loading} active >
                <List.Item.Meta
                />
              </Skeleton>
              <br></br>
              <hr />
              <br></br>
              <Skeleton loading={loading} active >
                <List.Item.Meta
                />
              </Skeleton>
              <br></br>
              <hr />

              <br></br>
              <Skeleton loading={loading} active >
                <List.Item.Meta
                />
              </Skeleton>
              <br></br>
              <hr />
              <br></br>
              <Skeleton loading={loading} active >
                <List.Item.Meta
                />
              </Skeleton>
              <br></br>
              <hr />
              <br></br>

              <Skeleton loading={loading} active >
                <List.Item.Meta
                />
              </Skeleton>
              <br></br>
              <hr />
              <br></br>
              <Skeleton loading={loading} active >
                <List.Item.Meta
                />
              </Skeleton>
            </div>
          ) :
          (
            <div className="srWrapperInner">
              {requestData.request_type === 'Manual request' ?
                (
                  <Card className="reportCard">
                    <div className="reportCardBody">
                      <Empty description="NO DATA AVAILABLE FOR MANUAL REQUESTS! - YOU HAVE NOT REQUESTED AN INSPECTION BY FILLING THE FORM THROUGH THE WEBSITE." />
                    </div>
                  </Card>
                ) :
                requestData.request_type === 'Flooring dealer' || requestData.request_type === 'Trusted Dealer' ?
                  (
                    <Card className="reportCard">
                      <div className="reportCardBody">
                        <div className="inspectorInfo">
                          <Row gutter={30}>
                            <Col xs={24} md={8}>
                              <div className="inspectorDetails">
                                <div className="inspecDetailHead">
                                  <h5 className="inspcTitle">
                                    Client Type:- {requestData?.client_type}
                                  </h5>
                                </div>
                                <div className="inspcInfo"></div>
                              </div>
                            </Col>
                            <Col xs={24} md={8}>
                              <div className="inspectorDetails">
                                <div className="inspecDetailHead">
                                  <h5 className="inspcTitle">
                                    Claim Number:- {requestData?.claim_number}
                                  </h5>
                                </div>
                                <div className="inspcInfo"></div>
                              </div>
                            </Col>
                          </Row>
                        </div>

                        {/*  Dealer */}
                        <div className="inspectionDetails">
                          <Divider orientation="left">
                            <Typography.Text strong style={{ fontSize: '18px' }}>

                              Dealer
                            </Typography.Text>
                          </Divider>
                          <div className="reportData">
                            <Row gutter={16}>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Dealer Name</label>
                                  <p>{requestData?.dealer_name}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Dealer Address</label>
                                  <p>{requestData?.dealer_address}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Dealer Contact</label>
                                  <p>{requestData?.dealer_contact}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Dealer Phone Number</label>
                                  <p>{requestData?.dealer_phone ? requestData?.dealer_phone : " -"}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Dealer Email</label>
                                  <p>{requestData?.dealer_email}</p>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </div>

                        {/* Inspection Site */}
                        <div className="inspectionDetails">
                          <Divider orientation="left">
                            <Typography.Text strong style={{ fontSize: '18px' }}>

                              Inspection Site
                            </Typography.Text>
                          </Divider>
                          <div className="reportData">
                            <Row gutter={16}>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Homeowner Name</label>
                                  <p>{requestData?.eu_name}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={12}>
                                <div className="formGroup">
                                  <label>Address</label>
                                  <p>{requestData?.eu_address}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Zip Code</label>
                                  <p>{requestData?.eu_zip}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Phone Number</label>
                                  <p>{requestData?.homeowner_phone_number}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Second Phone Number</label>
                                  <p>{requestData?.homeowner_phone_number}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Email Address</label>
                                  <p>{requestData?.eu_email}</p>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </div>

                        {/* Product To Inspect  */}
                        <div className="inspectionDetails">
                          <Divider orientation="left">
                            <Typography.Text strong style={{ fontSize: '18px' }}>

                              Product To Inspect
                            </Typography.Text>
                          </Divider>
                          <div className="reportData">
                            <Row gutter={16}>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Manufacturer Name</label>
                                  <p>{requestData?.manufacturer_name}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Style Name/Style Number</label>
                                  <p>{requestData?.style_name}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Color</label>
                                  <p>{requestData?.color}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Quantity</label>
                                  <p>{requestData?.quanty}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Subfloor Type</label>
                                  <p>{requestData?.subfloor_type ? requestData?.subfloor_type : '-'}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Underlayment</label>
                                  <p>{requestData?.underlayment}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Moisture Testing</label>
                                  <p>{requestData?.moisture_testing}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Meter of Method</label>
                                  <p>{requestData?.meter_of_method}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Test Results</label>
                                  <p>{requestData?.test_results}</p>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </div>

                        {/* Flooring Category */}
                        <div className="inspectionDetails">
                          <Divider orientation="left">
                            <Typography.Text strong style={{ fontSize: '18px' }}>

                              Flooring Category
                            </Typography.Text>
                          </Divider>
                          <div className="reportData">
                            <Row gutter={16}>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Category</label>
                                  <p>{requestData?.category}</p>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </div>

                        {/* Concerns */}
                        <div className="inspectionDetails">
                          <Divider orientation="left">
                            <Typography.Text strong style={{ fontSize: '18px' }}>

                              Concerns
                            </Typography.Text>
                          </Divider>
                          <div className="reportData">
                            <Row gutter={16}>
                              <Col xs={24} md={24}>
                                <div className="formGroup">
                                  <label>Concerns</label>
                                  <p>{requestData?.concerns}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Type</label>
                                  <p>{requestData?.concern_type}</p>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </div>

                        {/* Installation Details */}
                        <div className="inspectionDetails">
                          <Divider orientation="left">
                            <Typography.Text strong style={{ fontSize: '18px' }}>

                              Installation Details
                            </Typography.Text>
                          </Divider>
                          <div className="reportData">
                            <Row gutter={16}>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Installed by</label>
                                  <p>{requestData?.installed_by}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Installation Date</label>
                                  <p>{requestData?.installation_date}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Date Problem Noticed</label>
                                  <p>{requestData?.problem_date}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Room Installed</label>
                                  <p>{requestData?.room_installed}</p>
                                </div>
                              </Col>
                              <Col xs={24} md={6}>
                                <div className="formGroup">
                                  <label>Comments</label>
                                  <p>{requestData?.comments}</p>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </div >
                        <Card className="innerCard mb-3">
                          <div className="inspectionDetails">
                            <Col xs={24} md={24}>
                              <div className="formGroup">
                                <label>Uploaded Photos :</label>
                                <Row gutter={16}>

                                  {attachments &&
                                    Object.keys(attachments).map((keyName, index) => (
                                      <Col xs={24} md={8} key={index} className="my-2">
                                        <Image
                                          src={attachments[keyName]}
                                          alt="image1"
                                          className="img-fluid"
                                          preview
                                        />
                                      </Col>
                                    ))}
                                </Row>
                              </div>
                            </Col>
                          </div>
                        </Card>
                        <Row gutter={16}>
                          {requestData?.installation_instructions ?
                            <Col xs={24} md={8}>
                              <div className="formGroup">
                                <label>Installation Instructions :</label>
                                <p>
                                  <a href={requestData?.installation_instructions} target="_blank" rel="noreferrer" className="downloadLink">View installation instructions</a>
                                </p>
                              </div>
                            </Col> : ''
                          }
                          {requestData?.maintenence_instructions ?
                            <Col xs={24} md={8}>
                              <div className="formGroup">
                                <label>Care And Maintenance Instructions :</label>
                                <p>
                                  <a href={requestData?.maintenence_instructions} target="_blank" rel="noreferrer" className="downloadLink">View care And maintenance instructions</a>
                                </p>
                              </div>
                            </Col> : ''
                          }
                          {requestData?.warranty_information ?
                            <Col xs={24} md={8}>
                              <div className="formGroup">
                                <label>Warranty Information :</label>
                                <p>
                                  <a href={requestData?.warranty_information} target="_blank" rel="noreferrer" className="downloadLink">View warranty information</a>
                                </p>
                              </div>
                            </Col> : ''
                          }
                          {requestData?.hard_surface_checklist ?
                            <Col xs={24} md={8}>
                              <div className="formGroup">
                                <label>Hardsurface Checklist :</label>
                                <p>
                                  <a href={requestData?.hard_surface_checklist} target="_blank" rel="noreferrer" className="downloadLink">Hardsurface Checklist</a>
                                </p>
                              </div>
                            </Col> : ''
                          }
                        </Row>
                      </div >
                    </Card >
                  ) :
                  requestData.request_type === 'Flooring Manufacturer' ?
                    (
                      < FlooringManufacturerRequest
                        requestData={requestData}
                        setRequestData={setRequestData}
                        attachments={attachments}
                      />

                    ) :
                    requestData.request_type === 'Home owner' ?
                      (
                        <Card className="reportCard">
                          <div className="reportCardBody">
                            <div className="inspectorInfo">
                              <Row gutter={30}>
                                {/* <Col xs={24} md={4}>
                            <div className="inspectorLogo">
                              <Image
                                src={Logo}
                                className="img-fluid"
                                alt="InspectorLogo"
                                preview={false}
                              />
                            </div>
                          </Col> */}
                                <Col xs={24} md={6}>
                                  <div className="inspectorDetails">
                                    <div className="inspecDetailHead">
                                      <h5 className="inspcTitle">
                                        Client Type:- {requestData?.client_type}
                                      </h5>
                                    </div>
                                    <div className="inspcInfo"></div>
                                  </div>
                                </Col>
                              </Row>
                            </div>

                            {/*  Inspection Site */}
                            <div className="inspectionDetails">
                              <Divider orientation="left">
                                <Typography.Text strong style={{ fontSize: '18px' }}>

                                  Inspection Site
                                </Typography.Text>
                              </Divider>
                              <div className="reportData">
                                <Row gutter={16}>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Homeowner Name</label>
                                      <p>{requestData?.eu_name}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <div className="formGroup">
                                      <label>Address</label>
                                      <p>{requestData?.eu_address ? requestData?.eu_address : '-'}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Zip Code</label>
                                      <p>{requestData?.eu_zip}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Phone Number</label>
                                      <p>{requestData?.eu_phone2}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Second Phone Number</label>
                                      <p>{requestData?.eu_phone2}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Email Address</label>
                                      <p>{requestData?.eu_email}</p>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </div>

                            {/* Dealer  */}
                            <div className="inspectionDetails">
                              <Divider orientation="left">
                                <Typography.Text strong style={{ fontSize: '18px' }}>

                                  Dealer
                                </Typography.Text>
                              </Divider>
                              <div className="reportData">
                                <Row gutter={16}>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Dealer Name</label>
                                      <p>{requestData?.dealer_name}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Dealer Contact</label>
                                      <p>{requestData?.dealer_contact}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Dealer Phone Number</label>
                                      <p>{requestData?.dealer_phone}</p>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </div>

                            {/* Product to Inspect */}
                            <div className="inspectionDetails">
                              <Divider orientation="left">
                                <Typography.Text strong style={{ fontSize: '18px' }}>

                                  Product to Inspect
                                </Typography.Text>
                              </Divider>
                              <div className="reportData">
                                <Row gutter={16}>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Manufacturer Name</label>
                                      <p>{requestData?.manufacturer_name}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Style Name/Number</label>
                                      <p>{requestData?.style_name ? requestData?.style_name : '-'}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Color</label>
                                      <p>{requestData?.color}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Quantity</label>
                                      <p>{requestData?.quanty}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Subfloor Type</label>
                                      <p>{requestData?.quanty}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Underlayment</label>
                                      <p>{requestData?.underlayment}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Moisture Testing</label>
                                      <p>{requestData?.moisture_testing}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Meter of Method</label>
                                      <p>{requestData?.meter_of_method}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Test Results</label>
                                      <p>{requestData?.test_results}</p>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </div >

                            {/* Flooring Category */}
                            < div className="inspectionDetails" >
                              <Divider orientation="left">
                                <Typography.Text strong style={{ fontSize: '18px' }}>

                                  Flooring Category
                                </Typography.Text>
                              </Divider >
                              <div className="reportData">
                                <Row gutter={16}>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Category</label>
                                      <p>{requestData?.category ? requestData?.category : '-'}</p>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </div >

                            {/* Concern */}
                            < div className="inspectionDetails" >
                              <Divider orientation="left">
                                <Typography.Text strong style={{ fontSize: '18px' }}>

                                  Concerns
                                </Typography.Text>
                              </Divider >
                              <div className="reportData">
                                <Row gutter={16}>
                                  <Col xs={24} md={24}>
                                    <div className="formGroup">
                                      <label>Concerns</label>
                                      <p>{requestData?.concerns}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Type</label>
                                      <p>{requestData?.concern_type}</p>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </div >

                            {/* Installation Details  */}
                            < div className="inspectionDetails" >
                              <Divider orientation="left">
                                <Typography.Text strong style={{ fontSize: '18px' }}>

                                  Installation Details
                                </Typography.Text>
                              </Divider >
                              <div className="reportData">
                                <Row gutter={16}>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Installed By</label>
                                      <p>{requestData?.installed_by}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Installation Date</label>
                                      <p>{requestData?.installation_date}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Date Problem Noticed</label>
                                      <p>{requestData?.problem_date}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Room Installed</label>
                                      <p>{requestData?.room_installed}</p>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </div >

                            {/* Number of adults */}
                            < div className="inspectionDetails" >
                              <Divider orientation="left">
                                <Typography.Text strong style={{ fontSize: '18px' }}>

                                  Numbers of Adults, Children, Dogs, Cats
                                </Typography.Text>
                              </Divider >
                              <div className="reportData">
                                <Row gutter={16}>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Number of Adults</label>
                                      <p>{requestData?.adults}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Number of Children</label>
                                      <p>{requestData?.childrens}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Number of Dogs</label>
                                      <p>{requestData?.dogs}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Number of Cats</label>
                                      <p>{requestData?.cats}</p>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </div >

                            {/* Type of Heating or Cooling */}
                            < div className="inspectionDetails" >
                              <Divider orientation="left">
                                <Typography.Text strong style={{ fontSize: '18px' }}>

                                  Type of Heating or Cooling
                                </Typography.Text>
                              </Divider >
                              <div className="reportData">
                                <Row gutter={16}>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Type of Heating and Cooling</label>
                                      <p>{requestData?.typeof_heating_cooling}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Is there and operating a humidifier on the furnace?</label>
                                      <p>{requestData?.humidifier_on_furnace}</p>
                                    </div>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <div className="formGroup">
                                      <label>Do you run a dehumidifier?</label>
                                      <p>{requestData?.dehumidifier}</p>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </div >

                            {/* Comments */}
                            < div className="inspectionDetails" >
                              <Divider orientation="left">
                                <Typography.Text strong style={{ fontSize: '18px' }}>

                                  Comments
                                </Typography.Text>
                              </Divider >
                              <div className="reportData">
                                <Row gutter={16}>
                                  <Col xs={24} md={24}>
                                    <div className="formGroup">
                                      <label>Comments</label>
                                      <p>{requestData?.comments}</p>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </div >
                            <Card className="innerCard mb-3">
                              <div className="inspectionDetails">
                                <Col xs={24} md={24}>
                                  <div className="formGroup">
                                    <label>Uploaded Photos :</label>
                                    <Row gutter={16}>
                                      {attachments &&
                                        Object.keys(attachments).map((keyName, index) => (
                                          <Col xs={24} md={8} key={index} className="my-2">
                                            <Image
                                              src={attachments[keyName]}
                                              alt="image1"
                                              className="img-fluid"
                                              preview
                                            />
                                          </Col>
                                        ))}
                                    </Row>
                                  </div>
                                </Col>
                              </div>
                            </Card>
                            <Row gutter={16}>
                              {requestData?.installation_instructions ?
                                <Col xs={24} md={8}>
                                  <div className="formGroup">
                                    <label>Installation Instructions :</label>
                                    <p>
                                      <a href={requestData?.installation_instructions} target="_blank" rel="noreferrer" className="downloadLink">View installation instructions</a>
                                    </p>
                                  </div>
                                </Col> : ''
                              }
                              {requestData?.maintenence_instructions ?
                                <Col xs={24} md={8}>
                                  <div className="formGroup">
                                    <label>Care And Maintenance Instructions :</label>
                                    <p>
                                      <a href={requestData?.maintenence_instructions} target="_blank" rel="noreferrer" className="downloadLink">View care And maintenance instructions</a>
                                    </p>
                                  </div>
                                </Col> : ''
                              }
                              {requestData?.warranty_information ?
                                <Col xs={24} md={8}>
                                  <div className="formGroup">
                                    <label>Warranty Information :</label>
                                    <p>
                                      <a href={requestData?.warranty_information} target="_blank" rel="noreferrer" className="downloadLink">View warranty information</a>
                                    </p>
                                  </div>
                                </Col> : ''
                              }
                            </Row>
                          </div >
                        </Card >
                      ) :
                      (
                        ''
                      )
              }
            </div >
          )
        }
      </div >
    </div >
  );
}

export default SingleInspectionRequest;
